// Billing.jsx
import { Component } from 'react';
import { UserContext } from "../UserContext";
import api from "../api/index";
import { withRouter } from "../utils/withRouter";
import { Modal, Table, Spin } from "antd";
import { DateTime } from "luxon";
import "./styles/Billing.css";

// Stripe (embedded Payment Element for card setup)
import { loadStripe } from "@stripe/stripe-js";
import { Elements, ElementsConsumer, PaymentElement } from "@stripe/react-stripe-js";
import LoadingComponent from './LoadingComponent';

// Load publishable key from env
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const TERMS_URL = "https://gotspare.com/vatm-terms";
const PRIVACY_URL = "https://gotspare.com/privacy-policy";

class Billing extends Component {
  static contextType = UserContext;

  constructor(props) {
    super(props);
    this.state = {
      // billing (Stripe)
      stripeSubscriptionId: null,
      stripeCustomerId: null,
      billingSetupLoading: false,
      billingSetupError: null,

      // data
      licensedLocations: [],
      availableLocations: [],
      availableLicenses: [],
      licenseSummary: null,
      subscriptionCost: null, // cents (from sagent table)
      nextBillingDate: null,

      currentBillingPeriod: null,
      dataLoading: true,
      searchQuery: "",
      sortKey: "alpha",
      sortDir: "asc",
      locationFilter: "all",

      // purchase flow (inline panel)
      purchaseMode: false,
      purchaseLoading: false,
      selectedLicenseCount: 1,
      selectedLocationIds: [],
      prorationPreview: null,

      // bulk selection (licensed table)
      selectedLicensedIds: [],
      selectedLocationIdsAll: [],
      assignLoading: false,

      // per-row delete spinner
      deleteLoading: null,

      // add card modal
      payModalOpen: false,
      setupClientSecret: null,
      payModalSubmitting: false,
      payModalError: null,

      remainingDays: null,
      totalDaysInMonth: null,
      acceptedLegal: false,
      acceptedRecurring: false
    };
  }

  closeSetupModal = () => this.setState({ setupModalOpen: false, setupStatus: null, setupMessage: null });
  goAddLicenses = () => {
    window.location.href = "/billing";
  };

  async componentDidMount() {
    const urlParams = new URLSearchParams(window.location.search);
    const setup = (urlParams.get("setup") || "").toLowerCase();
    const message = urlParams.get("message") || "Unknown error occurred.";

    if (setup === "success") {
      Modal.success({
        title: "Success!",
        content: "Your billing setup is complete. You may now add licenses to locations.",
        okText: "Add Licenses"
      });
    } else if (setup === "cancel" || setup === "cancelled") {
      Modal.confirm({
        title: "Checkout Canceled",
        content: "Checkout was canceled. You can try again anytime.",
        okText: "Try Again",
        cancelText: "Close",
        // (Legacy path; we now use embedded add-card modal instead of redirect)
        onOk: () => this.openAddCardModal(),
      });
    } else if (setup === "error") {
      Modal.error({
        title: "Setup Failed",
        content: `Failed to set up subscription: ${message}`,
        okText: "Close",
      });
    }

    if (setup) {
      const cleanUrl = window.location.origin + window.location.pathname;
      window.history.replaceState({}, "", cleanUrl);
    }

    await this.loadBillingData();
  }

  normalize = (s) => (s || "").toString().toLowerCase().trim();
  normalizeLicenseIds = (list) => {
    if (!Array.isArray(list)) return [];
    return list
      .map((item) => (typeof item === "object" && item ? item.license_id : item))
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value));
  };

  // ===== Data loading =====
  loadBillingData = async () => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const billingUrl =
        `${api.main}api/billing/?sagent_id=${this.context.user.id}` +
        `&tz=${encodeURIComponent(tz)}` +
        `&access_token=${encodeURIComponent(this.context.user.access_token)}`;

      const locationsUrl =
        `${api.main}api/billing/locations?sagent_id=${this.context.user.id}` +
        `&tz=${encodeURIComponent(tz)}` +
        `&access_token=${encodeURIComponent(this.context.user.access_token)}`;

      const summaryUrl =
        `${api.main}api/billing/licenses/summary?sagent_id=${this.context.user.id}` +
        `&access_token=${encodeURIComponent(this.context.user.access_token)}`;
  
      const [billingResp, locationsResp, summaryResp] = await Promise.all([
        fetch(billingUrl, { headers: { 'Content-Type': 'application/json' } }),
        fetch(locationsUrl, { headers: { 'Content-Type': 'application/json' } }),
        fetch(summaryUrl, { headers: { 'Content-Type': 'application/json' } }),
      ]);
  
      const billingData = await billingResp.json();
      const locationsData = await locationsResp.json();
      const summaryData = await summaryResp.json();
  
      this.setState({
        stripeSubscriptionId: billingData.stripe_subscription_id || null,
        stripeCustomerId: billingData.stripe_customer_id || null,
        subscriptionCost: billingData.subscription_price_cents ?? 9900,
        currentBillingPeriod: billingData.current_billing_period,
        nextBillingDate: billingData.next_billing_date || null,
        licensedLocations: locationsData.licensed_locations || [],
        availableLocations: locationsData.available_locations || [],
        availableLicenses: this.normalizeLicenseIds(
          summaryData?.available_license_ids ||
            locationsData.available_license_ids ||
            locationsData.available_licenses ||
            locationsData.unassigned_license_ids ||
            []
        ),
        licenseSummary: summaryData?.ok ? summaryData : null,
        dataLoading: false,
      });
  
      const now = DateTime.now().setZone('America/Los_Angeles');
      const endOfMonth = now.endOf("month");
      const firstOfNextMonth = endOfMonth.plus({ days: 1 }).startOf("day");
      const totalDaysInMonth = endOfMonth.day;
      const remainingDays = Math.floor(endOfMonth.diff(now.startOf("day"), "days").days) + 1;
  
      this.setState({
        remainingDays,
        totalDaysInMonth,
        nextBillingDate: firstOfNextMonth.toISODate()
      });
  
      return { billingData, locationsData };
    } catch (error) {
      this.setState({ dataLoading: false });
      return null;
    }
  };

  formatNextBilling = (isoDate, tz = Intl.DateTimeFormat().resolvedOptions().timeZone) => {
    const dt = DateTime.fromISO(isoDate, { zone: tz });
    return dt.isValid ? dt.toFormat("LLLL dd, yyyy") : isoDate;
  }

  // ===== Proration helpers (client preview only; server/Stripe charges real) =====
  calculateProration = (licenseCount = 1, tz) => {
    const zone = tz || "America/Los_Angeles";
    let perLicenseCents = this.state.subscriptionCost ?? 9900;
    perLicenseCents = Number(perLicenseCents || 0);

    const now = DateTime.now().setZone(zone);
    const startOfMonth = now.startOf("month");
    const endOfMonth = now.endOf("month");
    const firstOfNextMonth = endOfMonth.plus({ days: 1 }).startOf("day");
    const totalDaysInMonth = endOfMonth.day;
    const fullMonth = now.hasSame(startOfMonth, "day");
    const remainingDays = Math.floor(endOfMonth.diff(now.startOf("day"), "days").days) + 1;

    const baseCents = perLicenseCents * licenseCount;
    const amountCents = fullMonth
      ? baseCents
      : Math.max(1, Math.round(baseCents * (remainingDays / totalDaysInMonth)));
    return {
      remainingDays,
      totalDaysInMonth,
      proratedAmountPerLicense: ((perLicenseCents * (remainingDays / totalDaysInMonth)) / 100).toFixed(2),
      totalProratedCost: (amountCents / 100).toFixed(2),
      fullMonthlyAmount: (perLicenseCents * licenseCount / 100).toFixed(2),
      nextBillingDate: firstOfNextMonth.toISODate()
    };
  };

  togglePurchaseMode = () => {
    const { purchaseMode, availableLocations, stripeCustomerId } = this.state;
    const canPurchase = stripeCustomerId && availableLocations.length > 0;

    if (!purchaseMode && !canPurchase) {
      return;
    }

    this.setState(
      (prev) => ({
        purchaseMode: !prev.purchaseMode,
        selectedLicenseCount: !prev.purchaseMode ? 1 : prev.selectedLicenseCount,
        selectedLocationIds: !prev.purchaseMode ? [] : prev.selectedLocationIds,
      }),
      () => {
        if (this.state.purchaseMode) {
          this.updateProrationPreview(1);
        }
      }
    );
  };

  updateProrationPreview = async (licenseCount) => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    try {
      const url = `${api.main}api/billing/licenses/quote` +
  `?sagent_id=${encodeURIComponent(this.context.user.id)}` +
  `&add_count=${encodeURIComponent(licenseCount)}` +
  `&tz=${encodeURIComponent(tz)}` +
  `&access_token=${encodeURIComponent(this.context.user.access_token)}`;

      const resp = await fetch(url);
      const data = await resp.json();

      if (!resp.ok || data.ok === false) throw new Error(data.error || "Preview failed");
      this.setState({
        prorationPreview: {
          totalProratedCost: data.estimate_dollars,
          fullMonthlyAmount: (
            (Number(this.state.subscriptionCost ?? 9900)) * licenseCount / 100
          ).toFixed(2),
        },
      });
    } catch (e) {
      const fallback = this.calculateProration(licenseCount, tz);
      this.setState({ 
        prorationPreview: fallback
      });
    }
  };

  // ===== Add Payment Method (create customer + setup intent) =====
  openAddCardModal = async () => {
    // prevent opening twice while loading
    if (this.state.billingSetupLoading) return;

    try {
      this.setState({
        billingSetupLoading: true,
        payModalOpen: true,
        payModalError: null,
        // clear any old client secret so we never reuse a stale SI
        setupClientSecret: null,
      });

      const resp = await fetch(`${api.main}api/billing/create_customer_and_setup_intent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sagent_id: this.context.user.id,
          access_token: this.context.user.access_token,
        }),
      });

      const data = await resp.json();
      if (!resp.ok || data.ok === false || !data.setup_intent_client_secret) {
        throw new Error(data.error || "Failed to start card setup");
      }

      // only set the secret if the modal is still open
      if (this.state.payModalOpen) {
        this.setState({ setupClientSecret: data.setup_intent_client_secret });
      }
    } catch (err) {
      this.setState({
        payModalError: err.message || "Could not initialize card setup.",
        payModalOpen: false,
        setupClientSecret: null,
      });
      Modal.error({
        title: "Payment Method Setup Failed",
        content: err.message || "Please try again.",
      });
    } finally {
      this.setState({ billingSetupLoading: false });
    }
  };


  confirmCardSetup = async (stripe, elements) => {
    if (!stripe || !elements) return;

    try {
      this.setState({ payModalSubmitting: true, payModalError: null });

      // 1) Let Payment Element validate inputs
      const submitResult = await elements.submit();
      if (submitResult?.error) {
        this.setState({ payModalError: submitResult.error.message, payModalSubmitting: false });
        return;
      }

      // guard against a stale/cleared client secret
      const { setupClientSecret } = this.state;
      if (!setupClientSecret) {
        this.setState({ payModalError: "Session expired. Please reopen the card form.", payModalSubmitting: false });
        return;
      }

      // 2) Confirm the SetupIntent with Payment Element
      const user = this.context?.user || {};
      const { setupIntent, error } = await stripe.confirmSetup({
        elements,
        clientSecret: setupClientSecret,
        redirect: "if_required",
        // Optional: pass whatever billing details you collect elsewhere
        confirmParams: {
          payment_method_data: {
            billing_details: {
              name: user?.name || undefined,
              email: user?.email || undefined,
              // address: { line1, city, state, postal_code, country }
            },
          },
        },
      });

      if (error) {
        this.setState({ payModalError: error.message, payModalSubmitting: false });
        return;
      }

      const pmId = setupIntent?.payment_method;
      if (!pmId || typeof pmId !== "string") {
        this.setState({ payModalError: "No payment method returned from Stripe.", payModalSubmitting: false });
        return;
      }

      // 3) Persist as default for the Customer
      const resp = await fetch(`${api.main}api/billing/set_default_payment_method`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sagent_id: this.context.user.id,
          payment_method_id: pmId,
          access_token: this.context.user.access_token,
        }),
      });

      const data = await resp.json();
      if (!resp.ok || data.ok === false) {
        this.setState({ payModalError: data.error || "Failed to save card.", payModalSubmitting: false });
        return;
      }

      // 4) Success — refresh, close, toast
      await this.loadBillingData();
      this.setState({
        payModalOpen: false,
        setupClientSecret: null,
        payModalSubmitting: false,
      });

      Modal.success({
        title: "Card Saved",
        content: Boolean(this.state.stripeCustomerId)
          ? "Your payment method has been updated."
          : "Your payment method has been added. You can now purchase licenses.",
      });
    } catch (e) {
      this.setState({
        payModalError: e.message || "Something went wrong. Please try again.",
        payModalSubmitting: false,
      });
    }
  };

  // ===== Purchase selection (available locations) =====
  handleLocationSelection = (registrationId, selected) => {
    const { selectedLocationIds, selectedLicenseCount } = this.state;
    const regId = Number(registrationId);
    let newSelected = [...selectedLocationIds];

    if (selected) {
      if (!newSelected.includes(regId) && newSelected.length < selectedLicenseCount) {
        newSelected.push(regId);
      }
    } else {
      newSelected = newSelected.filter(id => id !== regId);
    }
    this.setState({ selectedLocationIds: newSelected });
  };

  clearAllSelections = () => this.setState({ selectedLocationIds: [] });

  // ===== Purchase =====
  purchaseLicenses = async () => {
    const { selectedLicenseCount, selectedLocationIds } = this.state;

    // if (selectedLocationIds.length !== selectedLicenseCount) {
    //   Modal.error({
    //     title: "Insufficient locations selected",
    //     content: `Please select exactly ${selectedLicenseCount} locations for your licenses.`
    //   });
    //   return;
    // }

    this.setState({ purchaseLoading: true });

    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const response = await fetch(`${api.main}api/billing/licenses/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sagent_id: this.context.user.id,
          // location_ids: selectedLocationIds.map(Number),
          license_count: this.state.selectedLicenseCount, 
          tz,
          access_token: this.context.user.access_token,
          accepted_legal: this.state.acceptedLegal,
          accepted_recurring: this.state.acceptedRecurring,
          terms_url: TERMS_URL,
          privacy_url: PRIVACY_URL
        })
      });

      const result = await response.json();
      if (!response.ok || result.ok === false) {
        throw new Error(result?.error || 'Failed to purchase licenses');
      }

      Modal.success({
        title: "Success!",
        content: `Successfully purchased ${selectedLicenseCount} license${selectedLicenseCount > 1 ? 's' : ''}!`
      });

      await this.loadBillingData();
      this.setState({
        purchaseMode: false,
        purchaseLoading: false,
        selectedLocationIds: [],
        selectedLicenseCount: 1,
        prorationPreview: null
      });
    } catch (error) {
      this.setState({ purchaseLoading: false });
      Modal.error({
        title: "Failed",
        content: error.message || 'Failed to purchase licenses. Please try again.',
        okText: "Close",
      });
    }
  };

  // ===== Single row delete/undo =====
  deleteLicense = (licenseId, locationName) => {
    const perLicenseDollars = ((Number(this.state.subscriptionCost ?? 9900)) / 100).toFixed(2);
    const nextBillingDate = this.formatNextBilling(this.state.nextBillingDate)
    Modal.confirm({
      title: `Cancel license for "${locationName}"?`,
      content: `Your monthly subscription for this license is paid until ${nextBillingDate}. If you would like to proceed with cancelling today, 
      the cancellation will not go into effect until the date listed above.`,
      okText: "Schedule Cancellation",
      cancelText: "Keep License",
      okType: "danger",
      onOk: () => {
        return new Promise(async (resolve, reject) => {
          this.setState({ deleteLoading: licenseId });
          try {
            const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const response = await fetch(`${api.main}api/billing/licenses/cancel`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                sagent_id: this.context.user.id,
                license_ids: [licenseId],
                tz,
                access_token: this.context.user.access_token
              })
            });

            const result = await response.json();
            if (!response.ok || !result.ok) {
              throw new Error(result?.error || "Failed to schedule cancellation");
            }

            await this.loadBillingData();
            resolve();
          } catch (error) {
            Modal.error({
              title: "Cancellation Failed",
              content: error.message || "Failed to cancel license. Please try again.",
              okText: "Close"
            });
            reject(error);
          } finally {
            this.setState({ deleteLoading: null });
          }
        });
      }
    });
  };

  undoLicense = async (licenseId) => {
    try {
      const response = await fetch(`${api.main}api/billing/licenses/cancel/undo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sagent_id: this.context.user.id,
          license_ids: [licenseId],
          access_token: this.context.user.access_token
        })
      });

      const result = await response.json();
      if (!response.ok || !result.ok) throw new Error(result?.error || 'Failed to undo cancellation');

      await this.loadBillingData();
    } catch (error) {
      Modal.error({
        title: "Undo Failed",
        content: 'Failed to undo cancellation. Please try again.',
        okText: "Close",
      });
    }
  };

  // ===== Bulk selection & actions (licensed table) =====
  toggleLicensedSelection = (licenseId, checked) => {
    this.setState(prev => {
      const set = new Set(prev.selectedLicensedIds);
      if (checked) set.add(licenseId); else set.delete(licenseId);
      return { selectedLicensedIds: Array.from(set) };
    });
  };

  selectAllLicensed = (filterFn = null) => {
    const { licensedLocations } = this.state;
    const ids = (filterFn ? licensedLocations.filter(filterFn) : licensedLocations).map(l => l.license_id);
    this.setState({ selectedLicensedIds: ids });
  };

  clearLicensedSelection = () => this.setState({ selectedLicensedIds: [] });

  bulkDeleteSelected = () => {
  const { selectedLicensedIds, licensedLocations } = this.state;

  // Only cancel licenses that are NOT already pending cancellation
  const deletables = licensedLocations
    .filter(l => selectedLicensedIds.includes(l.license_id) && Number(l.pending_cancellation) !== 1)
    .map(l => l.license_id);

  const skippedCount = licensedLocations
    .filter(l => selectedLicensedIds.includes(l.license_id) && Number(l.pending_cancellation) === 1)
    .length;

  if (deletables.length === 0) {
    Modal.error({
      title: "Invalid Input",
      content: "No licenses selected to be cancelled.",
      okText: "Close",
    });
    return;
  }

  const perLicenseDollars = ((Number(this.state.subscriptionCost ?? 9900)) / 100).toFixed(2);
  const nextBillingPretty = this.formatNextBilling(this.state.nextBillingDate);

  const lines = [
    `Your monthly subscription for these ${deletables.length} licenses is paid until ${nextBillingPretty}. If you would like to proceed with cancelling today, the cancellations will not go into effect until the date listed above.`];

  if (skippedCount > 0) {
    lines.push(
      ``,
      `Note: ${skippedCount} selected license${skippedCount > 1 ? "s are" : " is"} already scheduled for cancellation and will be ignored.`
    );
  }

  Modal.confirm({
    title: "Schedule end-of-month cancellation?",
    content: (<div style={{ whiteSpace: "pre-line" }}>
      {lines.join("\n")}
    </div>),
    okText: "Schedule Cancellations",
    cancelText: "Keep Licenses",
    okType: "danger",
    onOk: () => {
      // Return a Promise so AntD shows loading on the OK button
      return new Promise(async (resolve, reject) => {
        try {
          const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
          const resp = await fetch(`${api.main}api/billing/licenses/cancel`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              sagent_id: this.context.user.id,
              license_ids: deletables,
              tz,
              access_token: this.context.user.access_token,
            }),
          });

          const result = await resp.json();
          if (!resp.ok || !result.ok) {
            throw new Error(result?.error || "Bulk delete failed");
          }

          await this.loadBillingData();
          this.clearLicensedSelection();
          resolve(); // close confirm
        } catch (e) {
          Modal.error({
            title: "Cancellations Failed",
            content: e.message || "Failed to schedule cancellations. Please try again.",
            okText: "Close",
          });
          reject(e);
        }
      });
    },
  });
};

  bulkUndoSelected = async () => {
    const { selectedLicensedIds, licensedLocations } = this.state;
    const undoables = licensedLocations
      .filter(l => selectedLicensedIds.includes(l.license_id) && Number(l.pending_cancellation) === 1)
      .map(l => l.license_id);

    if (undoables.length === 0) {
      Modal.error({
        title: "Invalid Input",
        content: "Please select licenses to undo cancellation.",
      });
      return;
    }

    try {
      const resp = await fetch(`${api.main}api/billing/licenses/cancel/undo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sagent_id: this.context.user.id,
          license_ids: undoables,
          access_token: this.context.user.access_token
        })
      });
      const result = await resp.json();
      if (!resp.ok || !result.ok) throw new Error(result?.error || 'Bulk undo failed');

      await this.loadBillingData();
      this.clearLicensedSelection();
    } catch (e) {
      Modal.error({
        title: "Undo Failed",
        content: 'Failed to undo cancellations. Please try again.',
        okText: "Close",
      });
    }
  };

  // ===== Bulk selection & actions (locations table) =====
  getLocationId = (location) => {
    const raw = location?.location_id ?? location?.registration_id ?? location?.id;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  };

  clearLocationSelection = () => this.setState({ selectedLocationIdsAll: [] });

  submitLicenseAssignments = async ({ assign, unassign }) => {
    try {
      this.setState({ assignLoading: true });
      const resp = await fetch(`${api.main}api/billing/licenses/assignments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sagent_id: this.context.user.id,
          assign,
          unassign_license_ids: unassign,
          access_token: this.context.user.access_token,
        }),
      });

      const result = await resp.json();
      if (!resp.ok || !result.ok) throw new Error(result?.error || "Assignment failed");

      Modal.success({
        title: "License Updates Applied",
        content: `Assigned ${result.assigned_count ?? 0} license(s), unassigned ${result.unassigned_count ?? 0} license(s).`,
      });

      await this.loadBillingData();
      this.clearLocationSelection();
    } catch (e) {
      Modal.error({
        title: "Update Failed",
        content: e.message || "Failed to update licenses. Please try again.",
        okText: "Close",
      });
    } finally {
      this.setState({ assignLoading: false });
    }
  };

  assignSelectedLicenses = () => {
    const { availableLicenses, selectedLocationIdsAll } = this.state;
    const { assignRows } = this.getSelectedLocationBuckets();

    if (assignRows.length === 0) {
      Modal.error({
        title: "Invalid Input",
        content: "Select at least one unlicensed location to assign.",
      });
      return;
    }

    const effectiveAvailableLicenses =
      availableLicenses.length > 0
        ? availableLicenses
        : this.normalizeLicenseIds(this.state.licenseSummary?.available_license_ids || []);
    if (effectiveAvailableLicenses.length < assignRows.length) {
      Modal.error({
        title: "Not Enough Licenses",
        content: `You selected ${assignRows.length} location(s), but only ${effectiveAvailableLicenses.length} unassigned license(s) are available.`,
      });
      return;
    }

    const assignments = assignRows.map((row, index) => ({
      license_id: effectiveAvailableLicenses[index],
      location_id: row.location_id,
    }));

    this.submitLicenseAssignments({ assign: assignments, unassign: [] });
  };

  unassignSelectedLicenses = () => {
    const { unassignRows } = this.getSelectedLocationBuckets();

    if (unassignRows.length === 0) {
      Modal.error({
        title: "Invalid Input",
        content: "Select at least one licensed location to unassign.",
      });
      return;
    }

    const unassign = unassignRows.map((row) => row.license_id).filter(Boolean);
    if (unassign.length === 0) {
      Modal.error({
        title: "Invalid Input",
        content: "Selected locations do not have licenses to unassign.",
      });
      return;
    }

    this.submitLicenseAssignments({ assign: [], unassign });
  };

  getSelectedLocationBuckets = () => {
    const { selectedLocationIdsAll } = this.state;
    const allRows = this.getLocationsTableData();
    const selected = allRows.filter((row) => selectedLocationIdsAll.includes(row.location_id));
    return {
      assignRows: selected.filter((row) => row.license_status === "unlicensed"),
      unassignRows: selected.filter((row) => row.license_status === "licensed"),
    };
  };

  getLocationsTableData = () => {
    const { licensedLocations, availableLocations } = this.state;
    const licensed = licensedLocations
      .map((loc) => ({
        ...loc,
        location_id: this.getLocationId(loc),
        license_status: "licensed",
      }))
      .filter((loc) => loc.location_id !== null);
    const unlicensed = availableLocations
      .map((loc) => ({
        ...loc,
        location_id: this.getLocationId(loc),
        license_id: null,
        license_status: "unlicensed",
      }))
      .filter((loc) => loc.location_id !== null);
    return [...licensed, ...unlicensed];
  };

  // ===== UI helpers =====
  calculateMonthlyCost = () => {
    const perLicenseCents = Number(this.state.subscriptionCost ?? 9900);
    return ((this.state.licensedLocations.length * perLicenseCents) / 100).toFixed(2);
  };

  formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  formatAddress = (loc = {}) => {
    const {address, city, state, zipcode} = loc;
    const line1 = address || "";
    const line2Parts = [city, state && zipcode ? `${state} ${zipcode}` : state || ""];
    const line2 = line2Parts.filter(Boolean).join(", ");
    return [line1, line2].filter(Boolean).join(", ");
  }

  clampLicenseCount = (value) => {
    const max = Math.max(1, this.state.availableLocations.length);
    if (!Number.isFinite(value)) return 1;
    return Math.min(Math.max(1, value), max);
  };

  handleLicenseCountInput = (raw) => {
    let n = parseInt(raw, 10);
    if (Number.isNaN(n)) n = 1;
    // const clamped = this.clampLicenseCount(n);

    // let selectedLocationIds = this.state.selectedLocationIds;
    // if (selectedLocationIds.length > clamped) {
    //   selectedLocationIds = selectedLocationIds.slice(0, clamped);
    // }

    this.setState(
      {selectedLicenseCount: n}, 
        // { selectedLocationIds },
      () => this.updateProrationPreview(this.state.selectedLicenseCount)
    );
  };

  handleLicenseCountChange = (newCount) => { this.handleLicenseCountInput(newCount); };
  normalizeLicenseCountOnBlur = () => { this.handleLicenseCountInput(this.state.selectedLicenseCount); };

  getVisibleAvailableLocations = () => {
    const { availableLocations, searchQuery, sortKey, sortDir } = this.state;
    const q = (searchQuery || "").toLowerCase().trim();

    let list = availableLocations.filter((l) => {
      if (!q) return true;
      const hay = `${(l.name || "").toLowerCase()} ${this.formatAddress(l).toLowerCase()}`;
      return hay.includes(q);
    });

    list.sort((a, b) => {
      if (sortKey === "alpha") {
        const A = (a.name || "").toLowerCase();
        const B = (b.name || "").toLowerCase();
        if (A < B) return sortDir === "asc" ? -1 : 1;
        if (A > B) return sortDir === "asc" ?  1 : -1;
        return 0;
      } else {
        const aTime = new Date(a.created_at).getTime() || 0;
        const bTime = new Date(b.created_at).getTime() || 0;
        if (aTime < bTime) return sortDir === "asc" ? -1 : 1;
        if (aTime > bTime) return sortDir === "asc" ?  1 : -1;
        return 0;
      }
    });

    return list;
  };

  handleSearchChange = (e) => { this.setState({ searchQuery: e.target.value }); };
  handleSortKeyChange = (e) => { this.setState({ sortKey: e.target.value }); };
  toggleSortDir = () => { this.setState((s) => ({ sortDir: s.sortDir === "asc" ? "desc" : "asc" })); };
  handleLocationFilterChange = (e) => { this.setState({ locationFilter: e.target.value }); };

  render() {
    const {
      stripeSubscriptionId,
      stripeCustomerId,
      billingSetupLoading,
      billingSetupError,
      licensedLocations,
      availableLocations,
      availableLicenses,
      licenseSummary,
      purchaseMode,
      purchaseLoading,
      selectedLicenseCount,
      selectedLocationIds,
      dataLoading,
      prorationPreview,
      selectedLocationIdsAll,
      nextBillingDate,
      remainingDays,
      totalDaysInMonth,
      payModalOpen,
      setupClientSecret,
      payModalSubmitting,
      payModalError,
      assignLoading,
      locationFilter,
    } = this.state;

    const monthlyCost = this.calculateMonthlyCost();
    const perLicenseDollars = ((Number(this.state.subscriptionCost ?? 9900)) / 100).toFixed(2);
    const currentProration =
      prorationPreview ||
      this.calculateProration(selectedLicenseCount, Intl.DateTimeFormat().resolvedOptions().timeZone);

    const perLicenseCents = Number(this.state.subscriptionCost ?? 9900);
    const addedMonthlyDollars = (selectedLicenseCount * perLicenseCents / 100).toFixed(2);
    const nextMonthlyTotalDollars = (
      Number(this.calculateMonthlyCost()) + Number(addedMonthlyDollars)
    ).toFixed(2);
    const locationsTableData = this.getLocationsTableData().filter((row) => {
      if (locationFilter === "licensed") return row.license_status === "licensed";
      if (locationFilter === "unlicensed") return row.license_status === "unlicensed";
      return true;
    });
    const selectedBuckets = this.getSelectedLocationBuckets();
    const hasSelectedLicensed = selectedBuckets.unassignRows.length > 0;
    const hasSelectedUnlicensed = selectedBuckets.assignRows.length > 0;
    const availableLicenseCount = licenseSummary?.counts?.available ?? availableLicenses.length;

    if (dataLoading) {
      return <div className="loading billing-loading">
        <LoadingComponent />
      </div>;
    }

    // Flag: render full UI when a Customer exists
    const hasBillingProfile = Boolean(stripeCustomerId);
    const hasActiveSubscription = Boolean(stripeSubscriptionId);

    return (
      <div className="billing-page">
        {/* Full-screen loader during purchase */}
        {this.state.purchaseLoading && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(255,255,255,0.6)",
              backdropFilter: "blur(2px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2000
            }}
          >
            <Spin size="large" tip="Processing payment…" />
          </div>
        )}

        <div className="page-header">
          <div>
            <p className="page-eyebrow">Licenses</p>
            <h2>License Billing</h2>
            <p className="section-subtitle">
              Monitor your licensed locations, update payment methods, and purchase more licenses.
            </p>
          </div>
        </div>

        <section className="page-section billing-panel">
        {/* Monthly Billing Summary (only when subscription exists) */}
        {hasActiveSubscription && (
          <div className="billing-summary-card">
            <div className="billing-summary-header">
              <div>
                <p className="section-eyebrow" style={{color: "white"}}>Current Bill</p>
                <h3>${monthlyCost} / month</h3>
              </div>
              <div className="billing-summary-meta">
                <span>{licensedLocations.length} active licenses × ${perLicenseDollars}</span>
                {nextBillingDate && (
                  <span className="next-billing">
                    Next billing: {this.formatNextBilling(nextBillingDate)}
                  </span>
                )}
                <br></br>
                {licenseSummary?.counts && (
                  <span>
                    Active {licenseSummary.counts.active} • Scheduled Cancellations {licenseSummary.counts.scheduled_cancel} • Canceled {licenseSummary.counts.canceled} • Available {licenseSummary.counts.available}
                  </span>
                )}
                {/* {licenseSummary?.assignments && (
                  <span>
                    Assignments: {licenseSummary.assignments.length} • Available IDs: {(licenseSummary.available_license_ids || []).join(", ") || "—"}
                  </span>
                )} */}
              </div>
            </div>
          </div>
        )}

        {/* ===== Actions Row (Buy Licenses + Add/Update Card) ===== */}
        {/* First-time setup helper under the row */}
        {!hasBillingProfile && (
          <div className="alert alert-warning billing-alert">
            <h3 style={{ margin: 0 }}>Setup Required</h3>
            <p style={{ margin: '4px 0 0' }}>
              Add a payment method to your account. You will only be charged when you add licenses.
            </p>
            {billingSetupError && (
              <div className="error-message" style={{ marginTop: 6 }}>
                Setup Error: {billingSetupError}
              </div>
            )}
          </div>
        )}
        <div className="billing-actions-row">
          <button
            onClick={this.togglePurchaseMode}
            className="btn btn-primary"
            disabled={!purchaseMode && (!hasBillingProfile || availableLocations.length === 0)}
            title={
              !purchaseMode && availableLocations.length === 0
                ? "No unlicensed locations available."
                : purchaseMode
                  ? "Return to licensed locations list"
                  : "Purchase new licenses"
            }
          >
            {purchaseMode ? "View Licensed Locations" : "Buy Licenses"}
          </button>

          <button
            onClick={this.openAddCardModal}
            disabled={billingSetupLoading}
            className="btn btn-primary"
          >
            {billingSetupLoading ? 'Starting…' : (hasBillingProfile ? 'Update Card' : 'Add Card')}
          </button>

          <div style={{ flex: 1 }} />

          {availableLocations.length === 0 && (
            <span className="no-locations-message">
              No unlicensed locations available. Upload more locations to purchase licenses.
            </span>
          )}
          <div className="billing-meta">
            <span>{availableLocations.length} unlicensed locations</span>
          </div>
        </div>

        {/* Shared Payment Element Modal (used for both Add + Update) */}
        <Modal
          open={payModalOpen}
          title={hasBillingProfile ? "Update Payment Method" : "Add Payment Method"}
          onCancel={() =>
            this.setState({
              payModalOpen: false,
              setupClientSecret: null,
              payModalError: null,
            })
          }
          footer={null}
          destroyOnHidden
        >
          {!setupClientSecret ? (
            <div>Loading…</div>
          ) : (
            <Elements
              stripe={stripePromise}
              options={{ clientSecret: setupClientSecret, appearance: { theme: 'stripe' } }}
            >
              <ElementsConsumer>
                {({ stripe, elements }) => (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      this.confirmCardSetup(stripe, elements);
                    }}
                  >
                    <PaymentElement />
                    {payModalError && (
                      <div style={{ color: '#d9534f', marginTop: 8 }}>{payModalError}</div>
                    )}
                    <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() =>
                          this.setState({
                            payModalOpen: false,
                            setupClientSecret: null,
                            payModalError: null,
                          })
                        }
                        disabled={payModalSubmitting}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={!stripe || !elements || payModalSubmitting}
                      >
                        Save Card
                      </button>
                    </div>
                  </form>
                )}
              </ElementsConsumer>
            </Elements>
          )}
        </Modal>

        {/* Licensed Locations / Purchase Panel (unified) */}
        <div className="billing-content-card">

          {/* NOT purchasing -> Licensed table with bulk actions */}
          {!purchaseMode ? (
            <>
              <div className="section-heading" style={{ marginBottom: 12 }}>
                <div>
                  <p className="section-eyebrow">All Locations</p>
                  <h4>Locations ({locationsTableData.length})</h4>
                </div>
              </div>

              {locationsTableData.length > 0 ? (
                <>
                  <div className="bulk-actions" style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
                    <button
                      className="btn btn-primary"
                      onClick={this.clearLocationSelection}
                      disabled={selectedLocationIdsAll.length === 0}
                    >
                      Clear
                    </button>
                    <span style={{ marginLeft: 8 }}>Selected: {selectedLocationIdsAll.length}</span>
                    <span style={{ marginLeft: 8 }}>Unassigned licenses: {availableLicenseCount}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 12 }}>
                      <span>Filter:</span>
                      <select
                        value={locationFilter}
                        onChange={this.handleLocationFilterChange}
                        className="input"
                        style={{ minWidth: 160 }}
                      >
                        <option value="all">All locations</option>
                        <option value="licensed">Active licenses</option>
                        <option value="unlicensed">Unlicensed</option>
                      </select>
                    </div>
                    <div style={{ flex: 1 }} />

                    <button
                      className="btn btn-primary"
                      onClick={this.assignSelectedLicenses}
                      disabled={
                        assignLoading ||
                        selectedLocationIdsAll.length === 0 ||
                        locationFilter === "licensed" ||
                        hasSelectedLicensed
                      }
                      title="Assign available licenses to selected unlicensed locations"
                    >
                      {assignLoading ? "Updating..." : "Assign Licenses"}
                    </button>

                    <button
                      className="btn btn-primary"
                      onClick={this.unassignSelectedLicenses}
                      disabled={
                        assignLoading ||
                        selectedLocationIdsAll.length === 0 ||
                        locationFilter === "unlicensed" ||
                        hasSelectedUnlicensed
                      }
                      title="Unassign licenses from selected licensed locations"
                    >
                      {assignLoading ? "Updating..." : "Unassign Licenses"}
                    </button>
                  </div>

                  <div className="billing-table-wrapper show-scrollbar transfer-table-grid">
                    <Table
                      className="manage-locations-table"
                      size="middle"
                      rowKey="location_id"
                      dataSource={locationsTableData}
                      pagination={{
                        pageSize: 10,
                        showSizeChanger: false,
                        className: "billing-table-pagination",
                        showTotal: (total, range) =>
                          total > 0 ? `${range[0]}-${range[1]} of ${total}` : "0-0 of 0",
                      }}
                      rowSelection={{
                        selectedRowKeys: selectedLocationIdsAll,
                        onChange: (keys) => this.setState({ selectedLocationIdsAll: keys.map(Number) }),
                      }}
                      columns={[
                        {
                          title: "Location",
                          key: "location",
                          render: (_, record) => (
                            <div className="manage-location-cell">
                              <div className="manage-location-name">{record.name}</div>
                              <div className="manage-location-address">
                                {this.formatAddress(record) || "—"}
                              </div>
                            </div>
                          ),
                          sorter: (a, b) => (a.name || "").localeCompare(b.name || ""),
                        },
                        {
                          title: "Status",
                          key: "status",
                          render: (_, item) => {
                            if (item.license_status === "unlicensed") {
                              return <span className="manage-location-status manage-location-status--transfer">Unlicensed</span>;
                            }
                            const isPending = Number(item.pending_cancellation) === 1;
                            const date =
                              item.cancel_effective_at_local
                                ? new Date(item.cancel_effective_at_local).toLocaleDateString()
                                : null;

                            return isPending ? (
                              <span className="manage-location-status manage-location-status--transfer">
                                Scheduled Deletion{date ? ` • ${date}` : ""}
                              </span>
                            ) : (
                              <span className="manage-location-status manage-location-status--active">Active</span>
                            );
                          },
                        },
                        {
                          title: "Assigned",
                          dataIndex: "license_assigned_at",
                          sorter: (a, b) => new Date(a.license_assigned_at || 0) - new Date(b.license_assigned_at || 0),
                          render: (v) => (v ? new Date(v).toLocaleDateString() : "—"),
                          width: 160,
                        },
                      ]}
                    />
                  </div>
                </>
              ) : (
                <div className="empty-state">
                  <p>No locations available.</p>
                </div>
              )}
            </>
          ) : (
            // ====== PURCHASE MODE: inline proration + selection + confirm ======
            <div>
              {/* License Count */}
              <div className="license-count-section">
                <label>
                  Number of licenses to purchase:
                  <input
                    type="number"
                    min={1}
                    // max={Math.max(1, availableLocations.length)}
                    value={this.state.selectedLicenseCount}
                    onChange={(e) => this.handleLicenseCountInput(e.target.value)}
                    onBlur={this.normalizeLicenseCountOnBlur}
                    style={{ width: 120, marginLeft: 8 }}
                  />
                  {/* <small style={{ marginLeft: 8, color: '#6c757d' }}>
                    (max {availableLocations.length})
                  </small> */}
                </label>
              </div>

              {/* Proration Info */}
              {(() => {
                const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
                const current = prorationPreview || this.calculateProration(selectedLicenseCount, tz);
                return (
                  <>
                    <div className="proration-info">
                      <h4>Billing Information</h4>
                      <div className="proration-details">
                        <div className="proration-line">
                          <span>Today's Date:</span>
                          <span>{this.formatDate(new Date())}</span>
                        </div>
                        <div className="proration-line">
                          <span>Days remaining in month:</span>
                          <span>{remainingDays} of {totalDaysInMonth} days</span>
                        </div>
                        <div className="proration-line">
                          <span style={{ fontWeight: "bold" }}>Immediate prorated charge (est.):</span>
                          <span className="proration-amount">${current.totalProratedCost}</span>
                        </div>
                        <div className="proration-line">
                          <span>Full monthly rate (starting next month):</span>
                          <span>${current.fullMonthlyAmount}/month</span>
                        </div>
                      </div>
                    </div>

                    {/* Location Selection
                    <div className="location-selection-section">
                      <div className="section-heading">
                        <div>
                          <h4>
                            Select {selectedLicenseCount} location{selectedLicenseCount > 1 ? 's' : ''} to license
                          </h4>
                        </div>
                        <div className="selection-counter">
                          {this.state.selectedLocationIds.length} of {this.state.selectedLicenseCount} selected
                        </div>
                      </div>

                      <div className="quick-select-options">
                        <input
                          type="text"
                          placeholder="Search by name or address…"
                          value={this.state.searchQuery}
                          onChange={this.handleSearchChange}
                        />
                      </div>

                      {(() => {
                        const data = this.getVisibleAvailableLocations().map(loc => ({
                          key: Number(loc.registration_id),
                          registration_id: Number(loc.registration_id),
                          name: loc.name,
                          address: this.formatAddress(loc),
                          created_at: loc.created_at || null,
                        }));

                        const columns = [
                          {
                            title: 'Location Name',
                            dataIndex: 'name',
                            key: 'name',
                            sorter: (a, b) => (a.name || "").localeCompare(b.name || "")
                          },
                          {
                            title: 'Address',
                            dataIndex: 'address',
                            key: 'address',
                            sorter: (a, b) => (a.address || "").localeCompare(b.address || "")
                          },
                          {
                            title: 'Added',
                            dataIndex: 'created_at',
                            key: 'created_at',
                            render: (val) => (val ? new Date(val).toLocaleDateString() : '—'),
                            width: 140,
                            sorter: (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0),
                          },
                        ];

                        const rowSelection = {
                          selectedRowKeys: this.state.selectedLocationIds,
                          onChange: (nextKeys) => {
                            if (nextKeys.length > this.state.selectedLicenseCount) {
                              nextKeys = nextKeys.slice(0, this.state.selectedLicenseCount);
                            }
                            this.setState({ selectedLocationIds: nextKeys.map(Number) });
                          },
                          getCheckboxProps: (record) => {
                            const isSelected = this.state.selectedLocationIds.includes(record.registration_id);
                            const atCap = this.state.selectedLocationIds.length >= this.state.selectedLicenseCount;
                            return { disabled: !isSelected && atCap };
                          },
                        };

                        return (
                          <div className="billing-table-wrapper show-scrollbar transfer-table-grid billing-table--select">
                            <Table
                              className="manage-locations-table"
                              size="middle"
                              columns={columns}
                              dataSource={data}
                              rowSelection={rowSelection}
                              rowKey="registration_id"
                            pagination={{
                              pageSize: 10,
                            }}
                              rowClassName={(record) => {
                                const isSelected = this.state.selectedLocationIds.includes(record.registration_id);
                                const atCap = this.state.selectedLocationIds.length >= this.state.selectedLicenseCount;
                                const isDisabled = !isSelected && atCap;
                                return isDisabled ? 'disabled-checkbox-row' : '';
                              }}
                            />
                          </div>
                        );
                      })()}
                    </div> */}

                    {/* Purchase Summary + Actions */}
                    <div className="purchase-summary">
                      <div className="summary-section">
                        <h4>Charges Today (Estimated)</h4>
                        <div className="summary-line">
                          <span>
                            {selectedLicenseCount} prorated license(s)
                          </span>
                          <span className="cost">${current.totalProratedCost}</span>
                        </div>
                      </div>

                      <div className="summary-section">
                        <h4>Starting Next Month</h4>
                        <div className="summary-line">
                          <span>Current monthly cost:</span>
                          <span>${this.calculateMonthlyCost()}/month</span>
                        </div>
                        <div className="summary-line">
                          <span>New licenses:</span>
                          <span>
                            +${(selectedLicenseCount * ((Number(this.state.subscriptionCost ?? 9900)) / 100)).toFixed(2)}/month
                          </span>
                        </div>
                        <div className="summary-line total">
                          <span>New Monthly Total:</span>
                          <span className="total-cost">
                            ${nextMonthlyTotalDollars}/month
                          </span>
                        </div>
                      </div>

                      <div className="summary-note">
                        <small>
                          You will be charged the prorated amount based on the number of days the license is in use for this first month.
                          Starting {this.formatNextBilling(nextBillingDate)}, you'll be charged the full monthly rate.
                        </small>
                      </div>

                      {/* Terms + Consent */}
                      <div className="consent-block" style={{marginTop: 12}}>
                        <label style={{display:'flex', gap:8, alignItems:'flex-start'}}>
                          <input
                            type="checkbox"
                            checked={this.state.acceptedLegal || false}
                            onChange={(e) => this.setState({ acceptedLegal: e.target.checked })}
                          />
                          <span>
                            I agree to the{" "}
                            <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">Terms of Service</a>
                            {" "}and{" "}
                            <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
                          </span>
                        </label>

                        <label style={{display:'flex', gap:8, alignItems:'flex-start', marginTop:8}}>
                          <input
                            type="checkbox"
                            checked={this.state.acceptedRecurring || false}
                            onChange={(e) => this.setState({ acceptedRecurring: e.target.checked })}
                          />
                          <span>
                            I authorize recurring charges of <strong>${nextMonthlyTotalDollars}</strong> per month
                            starting <strong>{this.formatNextBilling(nextBillingDate)}</strong>, until I cancel.
                            I can cancel anytime in Billing or by contacting support.
                          </span>
                        </label>
                      </div>

                      {/* Final actions */}
                      <div className="modal-actions" style={{padding:0, marginTop:16}}>
                        <button
                          onClick={() => this.setState({ purchaseMode: false, prorationPreview: null })}
                          className="btn btn-primary"
                          disabled={purchaseLoading}
                        >
                          Cancel
                        </button>

                        <button
                          onClick={this.purchaseLicenses}
                          className="btn btn-primary proration-charge"
                          disabled={
                            purchaseLoading ||
                            // selectedLocationIds.length !== selectedLicenseCount ||
                            !this.state.acceptedLegal ||
                            !this.state.acceptedRecurring
                          }
                          title={(!this.state.acceptedLegal || !this.state.acceptedRecurring) ? "Please accept the terms to continue" : undefined}
                        >
                          <span>Charge ${currentProration.totalProratedCost} Now</span>
                        </button>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div>
        </section>

        {/* Subscription Info Footer */}
        {hasActiveSubscription && (
          <div className="billing-agreement-info">
            <small>Automatic billing enabled • Subscription: {stripeSubscriptionId}</small>
          </div>
        )}
      </div>
    );
  }
}

export default withRouter(Billing);
              
